// Authenticator (TOTP) management for the logged-in user, bound to their LOGTO
// account — the factor that actually gates the Logto login MFA. Add → scan the
// QR in an authenticator app → confirm the first code to bind it. Remove deletes
// it. Per-person: one authenticator works across every tenant the user belongs to.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ShieldCheck, ShieldOff, Smartphone } from 'lucide-react';
import { mfaApi, type MfaSetup } from '@/lib/inventory-api';

export function TwoFactorCard() {
  const { t } = useTranslation('components');
  const qc = useQueryClient();
  const { data: status } = useQuery({ queryKey: ['mfa-status'], queryFn: mfaApi.status, retry: false });
  const [setup, setSetup] = useState<MfaSetup | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ['mfa-status'] });
  const enabled = status?.hasTotp ?? false;

  const begin = async () => {
    setBusy(true); setMsg(null);
    try { setSetup(await mfaApi.setup()); } catch { setMsg({ tone: 'err', text: t('twoFactorCard.errors.enrollStart') }); }
    finally { setBusy(false); }
  };
  const confirm = async () => {
    setBusy(true); setMsg(null);
    try {
      await mfaApi.confirm(code.trim());
      setSetup(null); setCode(''); setMsg({ tone: 'ok', text: t('twoFactorCard.success.enabled') }); refresh();
    } catch { setMsg({ tone: 'err', text: t('twoFactorCard.errors.codeMismatch') }); }
    finally { setBusy(false); }
  };
  const disable = async () => {
    setBusy(true); setMsg(null);
    try {
      const totp = status?.factors.filter((f) => f.type === 'Totp') ?? [];
      for (const f of totp) await mfaApi.remove(f.id);
      setMsg({ tone: 'ok', text: t('twoFactorCard.success.disabled') }); refresh();
    } catch { setMsg({ tone: 'err', text: t('twoFactorCard.errors.disable') }); }
    finally { setBusy(false); }
  };

  return (
    <div className="tk-card p-5">
      <div className="mb-1 flex items-center gap-2">
        {enabled ? <ShieldCheck size={16} className="text-ok" /> : <Smartphone size={16} className="text-muted" />}
        <h3 className="text-sm font-semibold">{t('twoFactorCard.title')}</h3>
        {enabled && <span className="rounded-full bg-ok/15 px-2 py-0.5 text-xs text-ok">{t('twoFactorCard.status.enabled')}</span>}
      </div>
      <p className="mb-3 text-xs text-muted">{t('twoFactorCard.description')}</p>

      {msg && <div className={`mb-3 rounded-md px-3 py-2 text-xs ${msg.tone === 'ok' ? 'bg-ok/10 text-ok' : 'bg-critical/10 text-critical'}`}>{msg.text}</div>}

      {/* Enrollment flow */}
      {setup ? (
        <div className="space-y-3">
          <div className="flex items-center gap-4">
            <img src={setup.qrCode} alt={t('twoFactorCard.qrAlt')} className="h-32 w-32 rounded bg-white p-1" />
            <div className="text-xs text-muted">
              <p className="mb-1">{t('twoFactorCard.scanPrompt')}</p>
              <code className="break-all rounded bg-white/5 px-1.5 py-0.5 text-fg">{setup.secret}</code>
            </div>
          </div>
          <div className="flex items-end gap-2">
            <label className="flex-1 text-xs text-muted">{t('twoFactorCard.codeLabel')}
              <input className="tk-input mt-1 text-center tracking-[0.3em]" inputMode="numeric" value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="000000" />
            </label>
            <button className="tk-btn-primary" disabled={busy || code.length !== 6} onClick={confirm}>{t('twoFactorCard.actions.confirm')}</button>
            <button className="tk-btn-ghost" disabled={busy} onClick={() => { setSetup(null); setCode(''); }}>{t('twoFactorCard.actions.cancel')}</button>
          </div>
        </div>
      ) : enabled ? (
        <button className="tk-btn rounded-md bg-critical/90 px-3 text-white hover:bg-critical disabled:opacity-50" disabled={busy} onClick={disable}>
          <ShieldOff size={15} /> {t('twoFactorCard.actions.disable')}
        </button>
      ) : (
        <button className="tk-btn-primary" disabled={busy} onClick={begin}>{busy ? t('twoFactorCard.actions.starting') : t('twoFactorCard.actions.enable')}</button>
      )}
    </div>
  );
}
